/**
 * Sentry Session Replay configuration, shared by web-app and admin-app so both
 * portals mask PII identically (APP-24207 / APP-24208). Plain data only — the
 * `Sentry.replayIntegration()` call itself lives in each app's
 * `instrumentation-client.ts`, so this package stays free of a `@sentry/nextjs`
 * dependency (mirrors `posthogSessionRecordingConfig`).
 *
 * Replay strategy (project decision D8): Sentry captures a replay *only* when an
 * error occurs — never full sessions. Full-session recording is PostHog's
 * responsibility (see `posthogSessionRecordingConfig`). Hence
 * `replaysSessionSampleRate` is 0 and `replaysOnErrorSampleRate` is 1.0, and both
 * are constant across every environment: Sentry only initialises where a DSN is
 * set, which is already environment-gated, so no per-environment branching is
 * needed here.
 */

/** Root-level replay sample rates, spread into `Sentry.init({...})`. */
export const sentryReplaySampleRates = {
	// No full-session replays in Sentry — PostHog owns full session recording.
	replaysSessionSampleRate: 0,
	// Capture a replay for every error.
	replaysOnErrorSampleRate: 1.0,
};

/**
 * Privacy-first options for `Sentry.replayIntegration()`. Everything is masked
 * by default — this mirrors PostHog's `maskAllInputs` + `maskTextSelector: "*"`
 * so PII is masked uniformly across both tools. `maskAllText`, `maskAllInputs`
 * and `blockAllMedia` are already the SDK defaults; they are set explicitly so
 * they cannot be silently disabled later. Opt a specific element back into
 * visibility with the `.sentry-unmask` class or the `data-sentry-unmask`
 * attribute.
 */
export const sentryReplayIntegrationOptions = {
	maskAllText: true,
	maskAllInputs: true,
	blockAllMedia: true,
	unmask: [".sentry-unmask", "[data-sentry-unmask]"],
};
